import { ROUTES } from '@eliadmin/core/src/constants/routes';
import type { AvailableLocales } from '@eliadmin/core/src/stores/locale-store';
import type { CookieConsentConfig } from 'vanilla-cookieconsent';

type CookieConsentLocale = AvailableLocales;

type CookieConsentTranslationKey =
  | 'text272'
  | 'text994'
  | 'text995'
  | 'text996'
  | 'text997'
  | 'text998'
  | 'text999'
  | 'text1000'
  | 'text1001'
  | 'text1002'
  | 'text1003'
  | 'text1004'
  | 'text1005'
  | 'text1006'
  | 'text1007'
  | 'text1008'
  | 'text1009'
  | 'text1010'
  | 'text1011'
  | 'text1012'
  | 'text1013'
  | 'text1014'
  | 'text1015'
  | 'text1016'
  | 'text1017';

type CookieConsentTranslationMap = Record<
  CookieConsentTranslationKey,
  Partial<Record<CookieConsentLocale, string>>
>;

export const COOKIE_CONSENT_TRANSLATIONS: CookieConsentTranslationMap = {
text272: {sk: 'Zavrieť',en: 'Close'},
text994: {sk: 'Táto webstránka používa súbory cookies',en: 'This website uses cookies'},
text995: {sk: 'Na prispôsobenie obsahu a reklám, poskytovanie funkcií sociálnych médií a analýzu návštevnosti používame súbory cookie. Informácie o tom, ako používate naše webové stránky, poskytujeme aj našim partnerom v oblasti sociálnych médií, inzercie a analýzy. Títo partneri môžu príslušné informácie skombinovať s ďalšími údajmi, ktoré ste im poskytli alebo ktoré od vás získali, keď ste používali ich služby.',en: 'We use cookies to personalise content and ads, provide social media features and analyse traffic. We also provide information about how you use our website to our social media, advertising and analytics partners. These partners may combine the relevant information with other information that you have provided to them or that they have collected from you when you have used their services.'},
text996: {sk: 'Povoliť všetko',en: 'Enable everything'},
text997: {sk: 'Odmietnuť',en: 'Reject'},
text998: {sk: 'Prispôsobiť',en: 'Customize'},
text999: {sk: 'Nastavenie cookies',en: 'Cookies settings'},
text1000: {sk: 'Služby',en: 'Services'},
text1001: {sk: 'Vaše možnosti ochrany súkromia',en: 'Your privacy options'},
text1002: {sk: 'V tejto časti môžete vyjadriť niektoré preferencie týkajúce sa spracovania vašich osobných údajov. Možete kedykoľvek preskúmať a zmeniť vyjadrené voľby tým, že znovu zobrazíte tento panel prostredníctvom poskytnutého odkazu. Ak chcete odmietnuť svoj súhlas so špecifickými činnosťami spracovania opísanými nižšie, prepínače prepnite na vypnuté alebo použite tlačidlo „Odmietnuť všetko“ a potvrďte, že chcete uložiť svoje voľby.',en: 'In this section you can express some preferences regarding the processing of your personal data. You can review and change the expressed preferences at any time by revisiting this panel via the link provided. If you wish to refuse your consent to the specific processing activities described below, toggle the toggle switch to off or use the "Refuse All" button to confirm that you wish to save your choices.'},
text1003: {sk: 'Potrebné',en: 'Needed'},
text1004: {sk: 'Potrebné súbory cookie pomáhajú vytvárať použiteľné webové stránky tak, že umožňujú základné funkcie, ako je navigácia stránky a prístup k chráneným oblastiam webových stránok. Webové stránky nemôžu riadne fungovať bez týchto súborov cookies.',en: 'Necessary cookies help to create usable websites by enabling basic functions such as site navigation and access to protected areas of the website. The website cannot function properly without these cookies.'},
text1005: {sk: 'Štatistiky a analýzy',en: 'Statistics and analysis'},
text1006: {sk: 'Štatistické súbory cookies pomáhajú majiteľom webových stránok, aby pochopili, ako komunikovať s návštevníkmi webových stránok prostredníctvom zberu a hlásenia informácií anonymne.',en: 'Statistical cookies help website owners understand how to interact with website visitors by collecting and reporting information anonymously.'},
text1007: {sk: 'Tabuľka súborov cookies',en: 'Cookie table'},
text1008: {sk: 'Cookie',en: 'Cookie'},
text1009: {sk: 'Doména',en: 'Domain'},
text1010: {sk: 'Marketing',en: 'Marketing'},
text1011: {sk: 'Marketingové súbory cookies sa používajú na sledovanie návštevníkov na webových stránkach. Zámerom je zobrazovať reklamy, ktoré sú relevantné a pútavé pre jednotlivých užívateľov, a tým cennejšie pre vydavateľov a inzerentov tretích strán.',en: 'Marketing cookies are used to track visitors to the website. The intent is to serve ads that are relevant and engaging to individual users and thus more valuable to publishers and third-party advertisers.'},
text1012: {sk: 'Chcem vedieť viac',en: 'I want to know more'},
text1013: {sk: 'Pre získanie informácií o spracovaní osobných údajov a vašich právach v súvislosti s ochranou osobných údajov, nás môžte kontaktovať',en: 'For information about the processing of personal data and your rights in relation to the protection of personal data, you can contact us'},
text1014: {sk: 'tu.',en: 'here.'},
text1015: {sk: 'Funkčné',en: 'Functional'},
text1016: {sk: 'Funkčné súbory cookie pomáhajú vykonávať určité funkcie, ako je zdieľanie obsahu webovej stránky na platformách sociálnych médií, zbieranie spätnej väzby a ďalšie funkcie tretích strán.',en: 'Functional cookies help perform certain functions, such as sharing website content on social media platforms, collecting feedback and other third-party features.'},
text1017: {sk: 'Uložiť nastavenia',en: 'Save settings'}
};

function getCookieConsentText(locale: AvailableLocales, key: CookieConsentTranslationKey) {
  const translations = COOKIE_CONSENT_TRANSLATIONS[key];
  return translations[locale as CookieConsentLocale] || translations.en || key;
}

function createCookieConsentTranslator(locale: AvailableLocales) {
  return (key: CookieConsentTranslationKey) => getCookieConsentText(locale, key);
}

export const getCookieConsentConfig = (locale: AvailableLocales) => {
  const ct = createCookieConsentTranslator(locale);

  const config: CookieConsentConfig = {
    disablePageInteraction: false,
    autoShow: true,
    hideFromBots: true,
    mode: 'opt-in',

    guiOptions: {
      consentModal: {
        layout: 'box inline',
        position: 'bottom left',
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {},
      ads: {},
      functional: {},
    },

    language: {
      default: locale,
      translations: {
        [locale]: {
          consentModal: {
            title: ct('text994'),
            description: getConsentModalSummary(ct('text995')),
            acceptAllBtn: ct('text996'),
            acceptNecessaryBtn: ct('text997'),
            showPreferencesBtn: ct('text998'),
          },
          preferencesModal: {
            title: ct('text999'),
            acceptAllBtn: ct('text996'),
            acceptNecessaryBtn: ct('text997'),
            savePreferencesBtn: ct('text1017'),
            closeIconLabel: ct('text272'),
            serviceCounterLabel: ct('text1000'),
            sections: [
              {
                title: ct('text1001'),
                description: ct('text1002'),
              },
              {
                title: ct('text1003'),
                description: ct('text1004'),
                linkedCategory: 'necessary',
              },
              {
                title: ct('text1015'),
                description: ct('text1016'),
                linkedCategory: 'functional',
              },
              {
                title: ct('text1005'),
                description: ct('text1006'),
                linkedCategory: 'analytics',
              },
              {
                title: ct('text1010'),
                description: ct('text1011'),
                linkedCategory: 'ads',
              },
              {
                title: ct('text1012'),
                description: `${ct('text1013')} <a href="${ROUTES.CONTACT()}" target="_blank">${ct(
                  'text1014',
                )}</a>`,
              },
            ],
          },
        },
      },
    },
  };

  return config;
};

function getConsentModalSummary(description: string) {
  const firstSentence = description.match(/^.*?[.!?](?:\s|$)/u)?.[0]?.trim();
  return firstSentence || description;
}
